// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api/v1";
export const WS_URL = import.meta.env.VITE_WS_URL || "ws://localhost:3000";

// Storage Keys (using generic names to avoid brand-specific references)
export const STORAGE_KEYS = {
  AUTH_TOKEN: "gateway_token",
  REFRESH_TOKEN: "gateway_refresh_token",
  USER_DATA: "gateway_user",
  THEME: "gateway_theme",
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: "/auth/login",
  LOGOUT: "/auth/logout",
  ME: "/auth/me",
  VALIDATE: "/auth/validate",
  REFRESH: "/auth/refresh",

  // Dashboard
  STATS: "/admin/stats",

  // Orders
  ORDERS: "/admin/orders",
  ORDER_APPROVE: (orderId: string) => `/admin/orders/${orderId}/approve`,
  ORDER_REJECT: (orderId: string) => `/admin/orders/${orderId}/reject`,

  // Merchants
  MERCHANTS: "/admin/merchants",
  MERCHANT: (merchantId: string) => `/admin/merchants/${merchantId}`,
  MERCHANT_CREATE: "/admin/merchants",
  MERCHANT_UPDATE: (merchantId: string) => `/admin/merchants/${merchantId}`,
  MERCHANT_DELETE: (merchantId: string) => `/admin/merchants/${merchantId}`,
  MERCHANT_STATUS: (merchantId: string) =>
    `/admin/merchants/${merchantId}/status`,
  MERCHANT_SUSPEND: (merchantId: string) =>
    `/admin/merchants/${merchantId}/suspend`,
  MERCHANT_ACTIVATE: (merchantId: string) =>
    `/admin/merchants/${merchantId}/activate`,
  MERCHANT_REGENERATE: (merchantId: string) =>
    `/admin/merchants/${merchantId}/regenerate-credentials`,

  // Fraud
  FRAUD_QUEUE: "/admin/fraud-queue",
  FRAUD_ORDERS: "/admin/fraud-queue/orders",

  // Security
  IP_LISTS: "/admin/security/ip-lists",
  IP_BLACKLIST: "/admin/security/ip-blacklist",
  IP_WHITELIST: "/admin/security/ip-whitelist",
  IP_DELETE: (id: string) => `/admin/security/ip-lists/${id}`,
  REVOKE_TOKEN: "/admin/security/revoke-token",
  REVOKE_USER_TOKENS: "/admin/security/revoke-user-tokens",
  KILL_SWITCH: (name: string) => `/admin/security/kill-switch/${name}`,
  KILL_SWITCHES: "/admin/security/kill-switches",
  SECURITY_EVENTS: "/admin/security/events",
  SECURITY_STATS: "/admin/security/stats",

  // Audit Logs
  AUDIT_LOGS: "/admin/audit-logs",

  // Notifications
  NOTIFICATIONS: "/notifications",
  NOTIFICATIONS_UNREAD: "/notifications/unread",
  NOTIFICATION_READ: (id: string) => `/notifications/${id}/read`,
  NOTIFICATION_READ_ALL: "/notifications/mark-all-read",

  // Admin Users
  ADMIN_USERS: "/admin/users",
  ADMIN_USER: (id: string) => `/admin/users/${id}`,
  ADMIN_USER_TOGGLE_STATUS: (id: string) => `/admin/users/${id}/toggle-status`,

  // Agents
  AGENTS: "/admin/agents",
  AGENT: (id: string) => `/admin/agents/${id}`,
  AGENT_CREATE: "/admin/agents",
  AGENT_UPDATE: (id: string) => `/admin/agents/${id}`,
  AGENT_DELETE: (id: string) => `/admin/agents/${id}`,
  AGENT_SUSPEND: (id: string) => `/admin/agents/${id}/suspend`,
  AGENT_ACTIVATE: (id: string) => `/admin/agents/${id}/activate`,
  ADMIN_AGENT_SIMS: (id: string) => `/admin/agents/${id}/sims`,

  // SIMs
  SIMS: "/admin/sims",
  SIM: (id: string) => `/admin/sims/${id}`,
  SIM_CREATE: "/admin/sims",
  SIM_UPDATE: (id: string) => `/admin/sims/${id}`,
  SIMS_AVAILABLE: "/admin/sims/available",
  SIM_ASSIGN_MERCHANT: (id: string) => `/admin/sims/${id}/assign-merchant`,
  SIM_UNASSIGN_MERCHANT: (id: string, merchantId: string) =>
    `/admin/sims/${id}/unassign-merchant/${merchantId}`,
  SIM_MERCHANTS: (id: string) => `/admin/sims/${id}/merchants`,
  SIM_STATUS: (id: string) => `/admin/sims/${id}/status`,
  SIM_ACTIVATE: (id: string) => `/admin/sims/${id}/activate`,
  SIM_DEACTIVATE: (id: string) => `/admin/sims/${id}/deactivate`,
  SIM_BLACKLIST: (id: string) => `/admin/sims/${id}/blacklist`,
  SIMS_DISABLE_AGENT: (agentId: string) =>
    `/admin/sims/agents/${agentId}/disable-all`,

  // Devices
  DEVICES: "/admin/devices",
  DEVICE: (id: string) => `/admin/devices/${id}`,
  DEVICES_PENDING: "/admin/devices/pending",
  DEVICE_APPROVE: (id: string) => `/admin/devices/${id}/approve`,
  DEVICE_REJECT: (id: string) => `/admin/devices/${id}/reject`,
  DEVICE_SUSPEND: (id: string) => `/admin/devices/${id}/suspend`,
  DEVICE_UNSUSPEND: (id: string) => `/admin/devices/${id}/unsuspend`,
  DEVICE_ASSIGN_AGENT: (id: string) => `/admin/devices/${id}/assign-agent`,
  DEVICE_UNASSIGN_AGENT: (id: string) => `/admin/devices/${id}/unassign-agent`,
  DEVICE_STATS: "/admin/devices/stats/overview",

  // SMS Relay
  SMS_RELAY_LATEST: "/admin/sms-relay/latest",
  SMS_RELAY_UNPROCESSED: "/admin/sms-relay/unprocessed",
  SMS_RELAY_SEARCH: "/admin/sms-relay/search",
  SMS_RELAY_MARK_PROCESSED: (id: string) =>
    `/admin/sms-relay/${id}/mark-processed`,
  SMS_RELAY_MARK_UNPROCESSED: (id: string) =>
    `/admin/sms-relay/${id}/mark-unprocessed`,
  SMS_RELAY_STATS: "/admin/sms-relay/stats/overview",

  // SMS Logs (Transaction Matching & Fraud Detection)
  SMS_LOGS: "/admin/sms-logs",
  SMS_LOG: (id: string) => `/admin/sms-logs/${id}`,
  SMS_LOGS_STATS: "/admin/sms-logs/stats/realtime",
  SMS_LOGS_UNMATCHED: "/admin/sms-logs/unmatched/list",
  SMS_LOGS_FRAUD: "/admin/sms-logs/fraud/alerts",

  // System Health
  SYSTEM_HEALTH: "/admin/stats/health",
  SYSTEM_ALERTS: "/admin/stats/alerts",

  // Analytics
  ANALYTICS_SYSTEM: "/admin/analytics/system-overview",
  ANALYTICS_REVENUE: "/admin/analytics/revenue",
  ANALYTICS_AGENTS: "/admin/analytics/agents/overview",
  ANALYTICS_AGENT: (id: string) => `/admin/analytics/agents/${id}`,
  ANALYTICS_CHANNELS: "/admin/analytics/channels",

  // Enhanced Merchant Operations
  MERCHANT_ADJUST_BALANCE: (id: string) =>
    `/admin/merchants/${id}/adjust-balance`,
  MERCHANT_UPDATE_COMMISSION: (id: string) =>
    `/admin/merchants/${id}/commission`,
  MERCHANT_ORDERS: (id: string) => `/admin/merchants/${id}/orders`,

  // Enhanced Order Operations
  ORDER_FLAG: (id: string) => `/admin/orders/${id}/flag`,

  // Withdrawals
  WITHDRAWALS: "/admin/withdrawals",
  WITHDRAWAL_APPROVE: (id: string) => `/admin/withdrawals/${id}/approve`,
  WITHDRAWAL_REJECT: (id: string) => `/admin/withdrawals/${id}/reject`,

  // Transactions
  TRANSACTIONS: "/admin/transactions",

  // Webhooks
  WEBHOOKS: "/admin/webhooks",
  WEBHOOK_RETRY: (id: string) => `/admin/webhooks/${id}/retry`,

  // Fraud Queue Review
  FRAUD_REVIEW: (orderId: string) => `/admin/fraud-queue/${orderId}/review`,

  // System Settings
  SETTINGS: "/admin/settings",
  SETTINGS_UPDATE: "/admin/settings",

  // Commissions
  COMMISSIONS: "/admin/commissions",

  // Agent API
  AGENT_SIMS: "/agent/sims",

  // Data Export
  EXPORT_ORDERS: "/admin/export/orders",
  EXPORT_TRANSACTIONS: "/admin/export/transactions",
  EXPORT_COMMISSIONS: "/admin/export/commissions",
  EXPORT_MERCHANTS: "/admin/export/merchants",
  EXPORT_AGENTS: "/admin/export/agents",

  // Agent API
  AGENT_SIMS_REGISTER: "/agent/sims",

  // Merchant Dashboard API
  MERCHANT_DASHBOARD_STATS: "/merchant/dashboard/stats",
  MERCHANT_DASHBOARD_SIMS: "/merchant/dashboard/sims",
  MERCHANT_DASHBOARD_CASH_FLOW: "/merchant/dashboard/cash-flow",
  MERCHANT_DASHBOARD_TRANSACTIONS: "/merchant/dashboard/transactions",
  MERCHANT_DASHBOARD_ORDERS: "/merchant/dashboard/orders",
  MERCHANT_DASHBOARD_SETTINGS: "/merchant/dashboard/settings",
} as const;

// Rate Limits
export const RATE_LIMITS = {
  AUTH: 10, // requests per minute
  DEFAULT: 100, // requests per minute
} as const;

// Query Keys for React Query
export const QUERY_KEYS = {
  STATS: ["stats"],
  ORDERS: ["orders"],
  ORDER: (id: string) => ["orders", id],
  MERCHANTS: ["merchants"],
  MERCHANT: (id: string) => ["merchants", id],
  MERCHANT_ORDERS: (id: string) => ["merchants", id, "orders"],
  FRAUD_QUEUE: ["fraud-queue"],
  FRAUD_ORDERS: ["fraud-orders"],
  SECURITY_EVENTS: ["security-events"],
  SECURITY_STATS: ["security-stats"],
  IP_LISTS: ["ip-lists"],
  KILL_SWITCHES: ["kill-switches"],
  AUDIT_LOGS: ["audit-logs"],
  NOTIFICATIONS: ["notifications"],
  ADMIN_USERS: ["admin-users"],
  ADMIN_USER: (id: string) => ["admin-users", id],
  AGENTS: ["agents"],
  AGENT: (id: string) => ["agents", id],
  ADMIN_AGENT_SIMS: (id: string) => ["agents", id, "sims"],
  SIMS: ["sims"],
  SIM: (id: string) => ["sims", id],
  SIMS_AVAILABLE: ["sims", "available"],
  SIM_MERCHANTS: (id: string) => ["sims", id, "merchants"],
  DEVICES: ["devices"],
  DEVICE: (id: string) => ["devices", id],
  DEVICES_PENDING: ["devices", "pending"],
  DEVICE_STATS: ["device-stats"],
  SMS_RELAY: ["sms-relay"],
  SMS_RELAY_UNPROCESSED: ["sms-relay", "unprocessed"],
  SMS_RELAY_STATS: ["sms-relay", "stats"],
  SMS_LOGS: ["sms-logs"],
  SMS_LOG: (id: string) => ["sms-logs", id],
  SYSTEM_HEALTH: ["system-health"],
  SYSTEM_ALERTS: ["system-alerts"],
  ANALYTICS_SYSTEM: ["analytics", "system"],
  ANALYTICS_AGENTS: ["analytics", "agents"],
  ANALYTICS_AGENT: (id: string) => ["analytics", "agents", id],
  ANALYTICS_CHANNELS: ["analytics", "channels"],
  WITHDRAWALS: ["withdrawals"],
  WITHDRAWAL: (id: string) => ["withdrawals", id],
  TRANSACTIONS: ["transactions"],
  TRANSACTION: (id: string) => ["transactions", id],
  WEBHOOKS: ["webhooks"],
  WEBHOOK: (id: string) => ["webhooks", id],
  SETTINGS: ["settings"],
  COMMISSIONS: ["commissions"],
  AGENT_SIMS: ["agent-sims"],
  AGENT_SIMS_DETAIL: (id: string) => ["agent-sims", id],

  // Merchant Dashboard Query Keys
  MERCHANT_STATS: ["merchant-stats"],
  MERCHANT_SIMS: ["merchant-sims"],
  MERCHANT_CASH_FLOW: (period?: string) => ["merchant-cash-flow", period],
  MERCHANT_TRANSACTIONS: ["merchant-transactions"],
  MERCHANT_DASHBOARD_ORDERS: ["merchant-dashboard-orders"],
  MERCHANT_SETTINGS: ["merchant-settings"],
} as const;

// WebSocket Events
export const WS_EVENTS = {
  // Outgoing
  SUBSCRIBE: "subscribe",
  UNSUBSCRIBE: "unsubscribe",
  PING: "ping",

  // Incoming
  CONNECTED: "connected",
  PONG: "pong",
  ORDER_UPDATE: "order_update",
  FRAUD_ALERT: "fraud_alert",
  SMS_NOTIFICATION: "sms_notification",
  DEVICE_HEARTBEAT: "device_heartbeat",
  DEVICE_OFFLINE: "device_offline",
  RETROACTIVE_MATCH: "retroactive_match_found",

  // SMS Logs Events
  SMS_RECEIVED: "sms_received",
  SMS_MATCHED: "sms_matched",
  SMS_AUTO_APPROVED: "sms_auto_approved",
  SMS_FRAUD_DETECTED: "sms_fraud_detected",
  SMS_MANUAL_REVIEW: "sms_manual_review_required",
} as const;

// WebSocket Channels
export const WS_CHANNELS = {
  ORDERS: "orders",
  FRAUD: "fraud_alerts",
  SMS: "sms",
  DEVICES: "devices",
} as const;

// Order Status
export const ORDER_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
} as const;

// Merchant Status
export const MERCHANT_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
  INACTIVE: "inactive",
} as const;

// Agent Status
export const AGENT_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
  INACTIVE: "inactive",
} as const;

// SIM Status
export const SIM_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  BLACKLISTED: "blacklisted",
} as const;

// Device Status
export const DEVICE_STATUS = {
  ONLINE: "online",
  OFFLINE: "offline",
  DISABLED: "disabled",
  PENDING: "pending",
} as const;

// Operators
export const OPERATORS = {
  BKASH: "bKash",
  NAGAD: "Nagad",
  ROCKET: "Rocket",
} as const;

// Order Types
export const ORDER_TYPES = {
  DEPOSIT: "deposit",
  PAYOUT: "payout",
} as const;

// Fraud Risk Levels
export const RISK_LEVELS = {
  NONE: "none",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

// Error Codes
export const ERROR_CODES = {
  INVALID_CREDENTIALS: 1001,
  ACCOUNT_DISABLED: 1002,
  INVALID_TOKEN: 1003,
  TOKEN_EXPIRED: 1004,
  MISSING_AUTH_HEADER: 1005,
  MISSING_FIELD: 1101,
  INVALID_FORMAT: 1102,
  ORDER_NOT_FOUND: 1201,
  INVALID_STATUS: 1202,
  MERCHANT_NOT_FOUND: 1205,
  INSUFFICIENT_BALANCE: 1301,
  RATE_LIMIT_EXCEEDED: 1501,
  INTERNAL_ERROR: 9999,
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 100,
} as const;

// Cache Times (in milliseconds)
export const CACHE_TIME = {
  STATS: 1 * 60 * 1000, // 1 minute
  ORDERS: 5 * 60 * 1000, // 5 minutes
  MERCHANTS: 10 * 60 * 1000, // 10 minutes
  DEFAULT: 5 * 60 * 1000, // 5 minutes
} as const;

// Retry Configuration
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // Base delay in ms
  RETRY_MULTIPLIER: 2, // Exponential backoff multiplier
} as const;
